import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

const COLORS = {
  gray:   '#5B6B79',
  blue:   '#4680ff',
  red:    '#9E2B25',
  orange: '#F29E4C',
  green:  '#2CA87F',
  purple: '#7C3AED',
  teal:   '#0891b2',
}

const SETTINGS = [
  {
    label: 'Business Settings',
    desc:  'Clinic info, working hours & general preferences',
    icon:  'ti ti-building-store',
    color: COLORS.blue,
    path:  '/Admin/business_setting',
  },
  {
    label: 'Finance',
    desc:  'VAT, discounts, payment methods & currencies',
    icon:  'ti ti-coin',
    color: COLORS.green,
    path:  '/Admin/finance',
  },
  {
    label: 'Notification Center',
    desc:  'Alerts, appointment reminders & messaging',
    icon:  'ti ti-bell',
    color: COLORS.orange,
    path:  '/Admin/notification_center',
  },
  {
    label: 'Security',
    desc:  'User access control & password policies',
    icon:  'ti ti-shield-lock',
    color: COLORS.red,
    path:  '/Admin/security',
  },
  {
    label: 'Medical Safety',
    desc:  'Drug interactions & clinical safety rules',
    icon:  'ti ti-first-aid-kit',
    color: COLORS.purple,
    path:  '/Admin/msafety',
  },
  {
    label: "API's & Integration",
    desc:  'Third-party connections (super admin only)',
    icon:  'ti ti-plug',
    color: COLORS.teal,
    path:  '/Admin/api_integration',
  },
  {
    label: 'Users',
    desc:  'Manage staff accounts, roles & permissions',
    icon:  'ti ti-users',
    color: COLORS.blue,
    path:  '/Admin/ManageStaff',
  },
  {
    label: 'Audit Log',
    desc:  'Track all system activity — creates, edits & deletions',
    icon:  'ti ti-history',
    color: COLORS.purple,
    path:  '/Admin/audit-log',
  },
  {
    label: 'Database Cleanup',
    desc:  'Wipe selected tables to start a fresh deployment (admin only)',
    icon:  'ti ti-database-x',
    color: COLORS.red,
    path:  '/Admin/database_cleanup',
    adminOnly: true,
  },
]

const isAdminUser = () => {
  try {
    const raw = localStorage.getItem('userMedical')
    return !!raw && String(JSON.parse(raw)?.data?.role || '').toLowerCase() === 'admin'
  } catch {
    return false
  }
}

const SettingNew = () => {
  const navigate = useNavigate()
  const [hovered, setHovered] = useState(null)
  const isAdmin = useMemo(isAdminUser, [])
  const settings = useMemo(
    () => SETTINGS.filter(s => !s.adminOnly || isAdmin),
    [isAdmin]
  )

  return (
    <div className="pc-container">
      <div className="pc-content">

        {/* ── Page Header ── */}
        <div style={{
          background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0',
          padding: '16px 24px', marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10,
            background: `${COLORS.blue}15`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <i className="ti ti-settings" style={{ color: COLORS.blue, fontSize: 20 }} />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1a202c' }}>Settings</div>
            <div style={{ fontSize: 12, color: COLORS.gray }}>Manage your clinic configuration and preferences</div>
          </div>
        </div>

        {/* ── Settings Grid ── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: 16,
        }}>
          {settings.map((s, i) => {
            const isHov = hovered === i
            return (
              <button
                key={s.path}
                onClick={() => navigate(s.path)}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  background: '#fff',
                  border: `1.5px solid ${isHov ? s.color : '#e8ecf0'}`,
                  borderRadius: 12,
                  padding: '20px 20px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  transition: 'border-color 0.18s, box-shadow 0.18s, transform 0.12s',
                  boxShadow: isHov ? `0 4px 18px ${s.color}22` : '0 1px 4px rgba(0,0,0,0.04)',
                  transform: isHov ? 'translateY(-2px)' : 'none',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 16,
                }}
              >
                {/* Icon */}
                <div style={{
                  width: 44, height: 44, borderRadius: 10, flexShrink: 0,
                  background: isHov ? s.color : `${s.color}15`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'background 0.18s',
                }}>
                  <i className={s.icon} style={{
                    fontSize: 22,
                    color: isHov ? '#fff' : s.color,
                    transition: 'color 0.18s',
                  }} />
                </div>

                {/* Text */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 14, fontWeight: 700,
                    color: isHov ? s.color : '#1a202c',
                    marginBottom: 4, transition: 'color 0.18s',
                  }}>
                    {s.label}
                  </div>
                  <div style={{ fontSize: 12, color: COLORS.gray, lineHeight: 1.4 }}>
                    {s.desc}
                  </div>
                </div>

                {/* Arrow */}
                <i className="ti ti-chevron-right" style={{
                  fontSize: 16, color: isHov ? s.color : '#ccc',
                  alignSelf: 'center', flexShrink: 0, transition: 'color 0.18s',
                }} />
              </button>
            )
          })}
        </div>

      </div>
    </div>
  )
}

export default SettingNew
